// Logistikk / forsyning: LOGSTAT (min enhets forsyningsstatus) + forsynings-
// anmodninger (resupply). Deles i sanntid over hubben. Docked GNÅ-panel, ren DOM.
//
// DELTA-RAPPORTERING (FUNN «Del 9»): logistikkrapportering er i praksis ALLTID
// delta. Ammunisjonen er den samme som for en time siden, vannet er det samme,
// og det som har endret seg er én eller to klasser. Skjemaet åpner derfor med
// FORRIGE innsendte LOGSTAT, slik at operatøren bare endrer det som er endret.
//
// ⚠ Men bare når det FINNES en forrige. Finnes den ikke, står feltet på «…»
// (ikke oppgitt). Et oppdiktet forsyningstall er verre enn et tomt felt: en
// «green» ingen har sagt er grønn kan bli grunnlaget for å sende et lag videre
// uten etterforsyning.
import {
  LogStatSchema, ResupplyRequestSchema, worstLevel, sortResupply, nextResupplyStatus,
  SUPPLY_CLASS_LABEL, SUPPLY_LEVEL_COLOR, RESUPPLY_STATUS_LABEL,
  ResupplyPriority, PRECEDENCE_LABEL, PRECEDENCE_CODEWORD, precedenceOrder,
  type LogStat, type ResupplyRequest, type SupplyClass, type SupplyLevel,
  type ResupplyPriority as ResupplyPriorityId,
} from '@skade/contracts';
import { KONTEKST_FELT, type Kontekst } from '../kontekst.js';
import { KontekstBlokk } from './kontekstblokk.js';
import { alderTekst } from './hendelseslogg.js';

const CLASSES: SupplyClass[] = ['ammo', 'fuel', 'water', 'rations', 'medical', 'batteries'];
const LEVELS: SupplyLevel[] = ['green', 'amber', 'red', 'black'];
// Hastegrad: rekkefølge (høyest først) OG etiketter kommer fra contracts
// (`precedence.ts`) — samme kilde som RFI-panelet bruker. Før dette ble enum-ID-ene
// rendret RÅTT som etikett («immediate», «priority», «routine»): små bokstaver og
// engelsk, mens RFI-panelet skrev «FLASH / Immediate / Priority / Routine». Samme
// NATO-kodeord i tre skrivemåter. Begrunnelsen for at kodeordene IKKE oversettes
// står i `precedence.ts` — les den før du endrer teksten.
const PRIOS: ResupplyPriorityId[] = precedenceOrder(ResupplyPriority.options);

export interface LogisticsHooks {
  self: string;
  myUnit: string;
  getLogStats: () => LogStat[];
  getResupply: () => ResupplyRequest[];
  publishLogStat: (l: LogStat) => void;
  publishResupply: (r: ResupplyRequest) => void;
  /** Ferdig kontekst (hvem/hvor/når/enhet) — ÉN kilde. */
  kontekst: () => Kontekst;
  /** Tida sendes inn; panelet leser ikke klokka selv. */
  naa: () => number;
  /**
   * Egen enhets FORRIGE innsendte LOGSTAT, eller null om ingen finnes.
   * Kilden er den samme lista panelet viser — ingen egen kopi av tilstand.
   */
  forrigeLogStat?: () => LogStat | null;
}

export class LogisticsPanel {
  readonly el: HTMLElement;
  private open = false;
  private logEl!: HTMLElement;
  private reqEl!: HTMLElement;
  private levelSel = new Map<SupplyClass, HTMLSelectElement>();
  private blokk!: KontekstBlokk;
  private deltaNote!: HTMLElement;
  /** Klasser operatøren selv har endret i denne økta — de skal ikke tilbakestilles. */
  private rort = new Set<SupplyClass>();

  constructor(private hooks: LogisticsHooks) {
    this.el = this.build();
    // Også ved bygging, ikke bare ved `toggle()`: i et poppet-ut vindu står
    // panelet åpent fra første stund og `toggle()` kalles aldri.
    this.fyllFraForrige();
    this.render();
  }

  toggle() {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    if (this.open) { this.blokk.oppfrisk(); this.fyllFraForrige(); this.render(); }
  }

  private build(): HTMLElement {
    const p = div('report-composer logistics-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Logistikk / forsyning'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    // Kontekst: hvem rapporterer, for hvilken enhet, når og hvor. Ferdig utfylt.
    this.blokk = new KontekstBlokk({
      hent: () => this.hooks.kontekst(),
      posisjonLabel: 'Posisjon (DZ / leveringssted)',
    });
    p.append(this.blokk.el);

    // Min LOGSTAT
    p.append(span('report-field-label', 'Min LOGSTAT'));
    // Delta-linja sier ÆRLIG om skjemaet er forhåndsutfylt eller ikke, og hvor
    // gammel forrige melding er. Uten den kan operatøren ikke vite om «green»
    // er hans egen vurdering fra i går eller en verdi han nettopp satte.
    this.deltaNote = span('checklist-note', '');
    p.append(this.deltaNote);
    const grid = div('logistics-grid');
    for (const c of CLASSES) {
      const s = document.createElement('select');
      s.className = 'checklist-sel';
      const none = document.createElement('option'); none.value = ''; none.textContent = `${SUPPLY_CLASS_LABEL[c]} …`;
      s.append(none);
      for (const lv of LEVELS) { const o = document.createElement('option'); o.value = lv; o.textContent = `${SUPPLY_CLASS_LABEL[c]}: ${lv}`; s.append(o); }
      s.addEventListener('change', () => this.rort.add(c));
      this.levelSel.set(c, s);
      grid.append(s);
    }
    p.append(grid);
    const sendLog = document.createElement('button');
    sendLog.className = 'report-submit'; sendLog.textContent = 'Send LOGSTAT';
    sendLog.onclick = () => this.sendLogStat();
    p.append(sendLog);
    this.logEl = div('targetlist-list');
    p.append(this.logEl);

    // Forsynings-anmodning
    p.append(span('report-field-label', 'Ny forsynings-anmodning'));
    const classSel = sel(CLASSES, (c) => SUPPLY_CLASS_LABEL[c as SupplyClass]);
    const qty = document.createElement('input'); qty.className = 'report-input'; qty.placeholder = 'Mengde (f.eks. 2 kasser 5.56)';
    const prioSel = sel(PRIOS, (x) => PRECEDENCE_LABEL[x as ResupplyPriorityId]);
    const sendReq = document.createElement('button');
    sendReq.className = 'report-submit'; sendReq.textContent = 'Send anmodning';
    sendReq.onclick = () => {
      if (!qty.value.trim()) return;
      const now = this.hooks.naa();
      const parsed = ResupplyRequestSchema.safeParse({
        id: `res-${now.toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}`,
        // Anmoder = enheten fra kontekst-blokka (redigerbar), ellers som før.
        requester: this.blokk.verdi(KONTEKST_FELT.enhet) || this.hooks.myUnit || this.hooks.self,
        supplyClass: classSel.value as SupplyClass,
        quantity: qty.value.trim(), priority: prioSel.value as ResupplyPriorityId, status: 'open', tsMs: now, updatedMs: now,
      });
      if (parsed.success) { this.hooks.publishResupply(parsed.data); qty.value = ''; this.render(); }
    };
    p.append(labeled('Klasse', classSel), labeled('Mengde', qty), labeled('Hastegrad', prioSel), sendReq);
    this.reqEl = div('targetlist-list');
    p.append(this.reqEl);
    return p;
  }

  /**
   * Forhåndsutfyll LOGSTAT med FORRIGE innsendte status (delta-rapportering).
   *
   * Regler, i denne rekkefølgen:
   *  1. Har operatøren selv endret en klasse i denne økta, RØRES DEN IKKE.
   *  2. Finnes en forrige LOGSTAT, settes de klassene den hadde verdi for.
   *  3. Klasser forrige melding IKKE hadde verdi for, blir stående på «…».
   *     De skal ikke arve noe — «ikke oppgitt» er en ekte tilstand.
   *  4. Finnes ingen forrige melding, fylles INGENTING. Ingen gjetning.
   */
  private fyllFraForrige() {
    const forrige = this.hooks.forrigeLogStat?.() ?? null;
    if (!forrige) {
      this.deltaNote.textContent = 'Ingen tidligere LOGSTAT for din enhet — feltene står tomme (ingen verdier gjettes).';
      return;
    }
    for (const [c, s] of this.levelSel) {
      if (this.rort.has(c)) continue;
      s.value = forrige.levels[c] ?? '';
    }
    const alder = alderTekst(this.hooks.naa(), forrige.updatedMs || forrige.tsMs);
    this.deltaNote.textContent = `Forhåndsutfylt fra forrige LOGSTAT (${alder}) — endre bare det som er endret.`;
  }

  private sendLogStat() {
    const levels: Partial<Record<SupplyClass, SupplyLevel>> = {};
    for (const [c, s] of this.levelSel) if (s.value) levels[c] = s.value as SupplyLevel;
    const now = this.hooks.naa();
    // Enheten fra kontekst-blokka (redigerbar) eier meldings-id-en, slik at
    // delta-flettingen i hubben treffer samme enhet hver gang.
    const enhet = this.blokk.verdi(KONTEKST_FELT.enhet) || this.hooks.myUnit || this.hooks.self;
    const parsed = LogStatSchema.safeParse({
      id: `log-${enhet}`, unit: enhet,
      levels, reportedBy: this.hooks.self, tsMs: now, updatedMs: now,
    });
    if (parsed.success) {
      this.hooks.publishLogStat(parsed.data);
      // Sendt = ny baslinje. Neste åpning er et delta fra DENNE meldinga.
      this.rort.clear();
      this.fyllFraForrige();
      this.render();
    }
  }

  render() {
    // LOGSTAT-liste (per enhet, verste nivå som chip)
    this.logEl.innerHTML = '';
    const logs = this.hooks.getLogStats();
    if (!logs.length) this.logEl.append(span('checklist-note', 'Ingen LOGSTAT mottatt.'));
    for (const l of logs) {
      const row = div('targetlist-row');
      const w = worstLevel(l);
      const dot = document.createElement('span'); dot.className = 'checklist-dot';
      dot.style.background = w ? SUPPLY_LEVEL_COLOR[w] : '#8a8a8a';
      const main = div('checklist-main');
      main.append(span('checklist-item-title', l.unit));
      const bits = (Object.entries(l.levels) as [SupplyClass, SupplyLevel][]).map(([c, lv]) => `${SUPPLY_CLASS_LABEL[c]}:${lv}`);
      main.append(span('checklist-note', bits.join(' · ') || 'ingen data'));
      row.append(dot, main);
      this.logEl.append(row);
    }

    // Anmodninger
    this.reqEl.innerHTML = '';
    const reqs = sortResupply(this.hooks.getResupply());
    if (!reqs.length) this.reqEl.append(span('checklist-note', 'Ingen anmodninger.'));
    for (const r of reqs) {
      const row = div('targetlist-row');
      const main = div('checklist-main');
      main.append(span('checklist-item-title', `${SUPPLY_CLASS_LABEL[r.supplyClass]} — ${r.quantity}`));
      // Kodeord, ikke rå enum-id: `r.priority` er wire-verdien («immediate») og
      // hører ikke i et grensesnitt. Samme form som RFI-lista.
      main.append(span('checklist-note', `${r.requester} · ${PRECEDENCE_CODEWORD[r.priority]} · ${RESUPPLY_STATUS_LABEL[r.status]}`));
      const actions = div('targetlist-actions');
      const next = nextResupplyStatus(r.status);
      if (next) actions.append(btn(`→ ${RESUPPLY_STATUS_LABEL[next]}`, () => { this.hooks.publishResupply({ ...r, status: next, updatedMs: this.hooks.naa() }); this.render(); }));
      row.append(main, actions);
      this.reqEl.append(row);
    }
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function btn(text: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button'); b.className = 'targetlist-btn'; b.textContent = text; b.onclick = run; return b;
}
function labeled(label: string, control: HTMLElement): HTMLElement {
  const wrap = div('report-field'); wrap.append(span('report-field-label', label), control); return wrap;
}
function sel(options: string[], label: (v: string) => string): HTMLSelectElement {
  const s = document.createElement('select'); s.className = 'checklist-sel';
  for (const o of options) { const opt = document.createElement('option'); opt.value = o; opt.textContent = label(o); s.append(opt); }
  return s;
}
